import axios, {AxiosError, AxiosPromise, AxiosRequestConfig, AxiosResponse} from 'axios'
import { CsvDownload } from "@/types/APIResponse";
import { LoginUser } from "@/types/Component";
import { toCamel } from 'snake-camel';

export default class ApiClient {
    private store: any;
    private apiServerBaseUrl: string;
    private loginUrl: string;
    private tokenUrl: string;
    private apiCgBaseUrl: string;
    private isLoginInProgress = false;

    public constructor(store: any) {
        this.store = store
        this.apiServerBaseUrl = process.env.VUE_APP_API_URL_BASE
        this.loginUrl = process.env.VUE_APP_LOGIN_URL
        this.tokenUrl = process.env.VUE_APP_TOKEN_URL
        this.apiCgBaseUrl= process.env.VUE_APP_CG_API_URL
    }

    public async getUserProfile() {
        const data = toCamel(await this.profileRequest('get', '/profile')) as LoginUser;
        if(data.statusCode == 403){
            this.store.dispatch('doUpdateDeleteTenantAlertPopUp', true);
            this.store.dispatch('doUpdateLastTenantId',null)
        }
        else{
            this.store.dispatch('doUpdateDeleteTenantAlertPopUp', false);
        }
        this.store.dispatch('doUpdateLoginUser', data);
        this.redirectToTenantBasePath()
        return this.store.getters.loginUser;
    }

    public setTenantId() {
        if(this.getTenantId()) {
            this.store.dispatch('doUpdateLastTenantId', this.getTenantId());
        }
    }

    public redirectToTenantBasePath() {
        const loginUser =this.store.getters?.loginUser
        const webDefaultTenant = loginUser?.webDefaultTenantID;
        const defaultTenantId = loginUser?.tenantDetailsList.find(
            (item:any) => webDefaultTenant === item.id
          );
        const tenantDetailsList = loginUser?.tenantDetailsList;
        if(!tenantDetailsList.length){
            this.store.dispatch('doUpdateCurrentPageName', '');
            this.store.dispatch('doUpdateParentPageName', '');
            this.store.dispatch('doUpdateDeleteTenantAlertPopUp', false);
            window.alert("利用可能なテナントが存在しません。ログアウトします。");
            return location.href = "/oauth2/logout"
        }
        else{
            const defaultTenant = defaultTenantId ? webDefaultTenant : tenantDetailsList[0].id;
            if (!this.getTenantId() && defaultTenant) {
                this.store.dispatch('doUpdateDeleteTenantAlertPopUp', false);
                window.location.href = `/tenants/${defaultTenant}/`;
            } 
            else if (this.getTenantId() !== defaultTenant && this.store.getters.deleteTenantAlertPopUp == false) {
                window.location.href = `/tenants/${defaultTenant}/`;
            }
        }
    }

    public errorAlert(statusCord: number, message: string | undefined) {
        const alertMessage = 'リクエストに失敗しました。\n'
        if(message) {
            // サーバからエラーメッセージが返却されている場合はそれを表示する
            window.alert(alertMessage + message)
            return false
        }
        switch (statusCord) {
            case 404:
                window.alert(alertMessage + '対象のリソースが削除された可能性があります。')
                break
            case 409:
                window.alert(alertMessage + '名称が重複している可能性があります。')
                break
            case 428:
                window.alert(alertMessage + '他のユーザにより対象データが更新されています。')
                break
            case 400:
                window.alert(alertMessage + 'リクエスト内容が不正です。')
                break
            case 500:
                window.alert(alertMessage + '予期しないエラーです。サポートに連絡してください。')
                break
        }
    }

    public async profileRequest(method: "get"|"post"|"put"|"delete", url: string, params?: any) {
        const accessToken = this.store.getters.accsessToken
        let tenantId = this.getTenantId()
        // develop
        if(tenantId === '' && !this.store.getters.tenantId){
          tenantId = 'ae1db8ea36b74eee91b983aabb0294ec'
        }
        else{
            tenantId = this.store.getters.tenantId
        }
        const config: AxiosRequestConfig = {
            url: `${this.apiServerBaseUrl}${url}`,
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
                'agri-tenant-id': tenantId
            },
            data: params,
            withCredentials: true
        }
        try {
            const res = await axios(config);
            return res.data;
        } catch (error) {
            if (error.response.status === 401) {
                await this.getAccessToken()
            }
            else if (error.response.status === 404) {
                window.alert("利用可能なテナントが存在しません。ログアウトします。");
                return location.href = "/oauth2/logout"
            }
            else if(error.response.status === 400){
                window.alert('リクエスト内容が不正です。')
            }
            else if(error.response.status === 500){
                window.alert('予期しないエラーです。サポートに連絡してください。')
            }
            else if(error.response.status === 428){
                window.alert('他のユーザにより対象データが更新されています。')
            }
            else if(error.response.status === 409){
                window.alert('名称が重複している可能性があります。')
            }    
        }
    }

    public async doRequest(method: "get"|"post"|"put"|"delete", url: string, params?: any) {
        const accessToken = this.store.getters.accsessToken
        let tenantId = this.getTenantId()
        // develop
        if(tenantId === '' && !this.store.getters.tenantId){
          tenantId = 'ae1db8ea36b74eee91b983aabb0294ec'
        }
        else{
            tenantId = this.store.getters.tenantId
        }
        const config: AxiosRequestConfig = {
            url: `${this.apiServerBaseUrl}${url}`,
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
                'agri-tenant-id': tenantId
            },
            data: params,
            withCredentials: true
        }
        try {
            const res = await axios(config);
            return res.data;
        } catch (error) {
            if (error.response.status === 401) {
                await this.getAccessToken()
            }
            this.errorAlert(error.response.status, error.response.data?.message)
            return Promise.reject(error.response)
        }
    }

    public async cloudGearRequest(method: "get" | "post" | "delete", url: string, params?: any) {
        const accessToken = this.store.getters.accsessToken
        const config: AxiosRequestConfig = {
            url: `${this.apiCgBaseUrl}${url}`,
            method: method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
            },
            data: params,
        }
        try {
            const res = await axios(config);
            return res.data;
        } catch (error) {
            if (error.response.status === 401) {
                await this.getAccessToken()
            }
            return Promise.reject(error.response)

        }
    }

    public async fileUpload(url: string, file: any) {
        const accessToken = this.store.getters.accsessToken
        let tenantId = this.getTenantId()
        // develop
        if(tenantId === ''){
            tenantId = 'ae1db8ea36b74eee91b983aabb0294ec'
        }
        const formData = new FormData()
        formData.append('file', file)
        const config: AxiosRequestConfig = {
            url: `${this.apiServerBaseUrl}${url}`,
            method: 'post',
            headers: {
                'content-type': 'multipart/form-data',
                'Authorization': `Bearer ${accessToken}`,
                'agri-tenant-id': tenantId
            },
            data: formData,
            withCredentials: true
        }
        try {
            const res = await axios(config);
            return res.data;
        } catch (error) {
            if (error.response.status === 401) {
                await this.getAccessToken()
            }
            this.errorAlert(error.response.status, error.response.data?.message)
            return Promise.reject(error.response)
        }
    }

    public async getAccessToken(callback?: any): Promise<string> {
        return await axios.get(this.tokenUrl, {
            withCredentials: true
        })
        .then((res)=>{
            const data = res.data
            const status = res.status
            if(status === 200){
                this.store.dispatch('doUpdateSession', data)
                if(callback !== undefined){
                    callback()
                }
            } else {
                this.login()
            }
            return this.store.getters.accsessToken
        })
        .catch((error) => {
            this.login()
        })
    }

    public login(): void {
        if (this.isLoginInProgress) {
            return;
        }
        this.isLoginInProgress = true;
        const callbackUrl = encodeURI(window.location.href);
        window.location.replace(`${this.loginUrl}?callback_url=${callbackUrl}`);
    }

    public getTenantId(): string {
        // パスから取得
        const regstr = new RegExp('/tenants/([^/]+)');
        const regx = location.pathname.match(regstr);
        if(regx && regx.length > 0) {
            return regx[1];
        } else {
            return ''
        }
    }

    public getBase64(file: any) {
        // アップロードしたファイルをBase64形式に変換しPromiseで返す
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.readAsDataURL(file)
            reader.onload = () => resolve({ base64: reader.result, file: file })
            reader.onerror = error => reject(error)
        })
    }

    public fileSizeFormat(bytes: number) {
        const sizes: string[] = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        if (bytes == 0) return '0 Byte'
        const i: number = parseInt(String(Math.floor(Math.log(bytes) / Math.log(1024))))
        return Math.round(bytes / Math.pow(1024, i)) + ' ' + sizes[i]
    }

    public isSizeOver(fileList: any): boolean {
        // サイズチェックしてオーバーしていたらtrueを返しアラートを表示する
        let isSizeOver = false
        const sizeLimit = 20971520 // 20MB

        const files = Array.from(fileList)

        files.forEach((file: any) => {
            if (sizeLimit && file.size > sizeLimit) {
                isSizeOver = true
            }
        })

        if (isSizeOver) {
            const maxFileSize = this.fileSizeFormat(sizeLimit)
            window.alert(
                `アップロードできるファイルの最大サイズは${maxFileSize}です`
            )
            return true
        }else {
            return false
        }
    }

    public isImageFile(fileName: string): boolean {
        // ファイルが画像だったらtrue、それ以外だったらfalseを返す
        const f = fileName.split('.')
        const extension = f[f.length-1].toLowerCase()
        if( extension == 'bmp' || extension == 'gif' || extension == 'jpeg' || extension == 'jpg' || extension == 'tiff' || extension == 'tif' || extension == 'png' ) {
            return true
        }else {
            return false
        }
    }

    // returnにdataだけでなくheaderなどを含めた全てを返す
    public async getRequest(url: string, params?: any) {
        const accessToken = this.store.getters.accsessToken
        let tenantId = this.getTenantId()
        // develop
        if(tenantId === '' && !this.store.getters.tenantId){
            tenantId = 'ae1db8ea36b74eee91b983aabb0294ec'
        }
        else{
            tenantId = this.store.getters.tenantId
        }
        const config: AxiosRequestConfig = {
            url: `${this.apiServerBaseUrl}${url}`,
            method: 'get',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`,
                'agri-tenant-id': tenantId
            },
            data: params,
            withCredentials: true
        }
        try {
            const res = await axios(config);
            return res;
        } catch (error) {
            if (error.response.status === 401) {
                await this.getAccessToken()
            }
            this.errorAlert(error.response.status, error.response.data?.message)
            return Promise.reject(error.response)
        }
    }

    public csvDownload(response: CsvDownload): void {
        const bom = new Uint8Array([0xEF, 0xBB, 0xBF])
        const blob = new Blob([bom, response.data], {type: 'text/csv'})

        const link = document.createElement('a')
        const downloadUrl = window.URL.createObjectURL(blob)
        link.href = downloadUrl
        link.download = decodeURIComponent(response.headers["csv-file-name"])
        link.click()
        URL.revokeObjectURL(downloadUrl);
    }

    public validateInputInteger(event: InputEvent) {
        // 入力値を取得
        const data = event.data
        // 数値のみ許容
        const pattern = /^\d*$/;
        // 文字削除時はdataがnullになるためそのまま通す
        if(data != null && !pattern.test(data)) {
            // pattern外のときは入力をキャンセルする
            return event.preventDefault()
        }
    }

    public validateInputDecimal(event: InputEvent) {
        // 入力値を取得
        const data = event.data
        // 数値と小数点のみ許容
        const pattern = /^[.\d]*$/;
        // 文字削除時はdataがnullになるためそのまま通す
        if(data != null && !pattern.test(data)) {
            // pattern外のときは入力をキャンセルする
            return event.preventDefault()
        }
    }

    public exponentToDecimal(exponential: number) {
        return exponential.toFixed(10).toString().replace(/\.?0*$/, '')
    }
}